import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../util/hook";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes, faUsers } from "@fortawesome/free-solid-svg-icons";
import { analyticsService } from "../../services/AnalyticsService";

const MENU_ITEMS = [
  { label: "홈", href: "#" },
  { label: "칼럼/주일설교", href: "#sermon" },
  { label: "교회소개", href: "#about" },
  { label: "예배와모임", href: "#worship" },
  { label: "온라인헌금", href: "#offering" },
  { label: "위치안내", href: "#location" },
];

const Header = () => {
  const { isLoggedIn, login, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [totalVisitors, setTotalVisitors] = useState<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("");

  // 스크롤/클릭 충돌 방지용 Ref
  const isClickScroll = React.useRef(false);
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);

  const scrollWithOffset = (element: Element) => {
    const yOffset = -100;
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  React.useEffect(() => {
    if (location.pathname === "/") {
      if (location.hash) {
        // 해시가 있으면 해당 섹션 활성화 및 스크롤
        setActiveSection(location.hash);
        const element = document.querySelector(location.hash);
        if (element) {
          setTimeout(() => {
            scrollWithOffset(element);
            // 스크롤 완료 후 해시 제거
            setTimeout(() => {
              window.history.replaceState(null, "", "/");
            }, 1000);
          }, 500);
        }
      } else {
        // 해시 없으면 홈 활성화
        setActiveSection("#");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      setActiveSection("");
    }
  }, [location]);

  React.useEffect(() => {
    if (location.pathname !== "/") return;

    const observerOptions = {
      root: null,
      rootMargin: "-90px 0px -50% 0px",
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // 클릭으로 스크롤 중일 때는 감지 로직 무시
      if (isClickScroll.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
          }

          // "찰나의 시간차" (300ms) 후 메뉴 변경
          debounceTimer.current = setTimeout(() => {
            const id = entry.target.id;
            if (id) {
              setActiveSection(`#${id}`);
            } else if (window.scrollY < 100) {
              setActiveSection("#");
            }
          }, 300);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    MENU_ITEMS.forEach((item) => {
      if (item.href.startsWith("#") && item.href !== "#") {
        const element = document.querySelector(item.href);
        if (element) observer.observe(element);
      }
    });

    const handleScroll = () => {
      if (isClickScroll.current) return;
      if (window.scrollY < 100) {
        // 맨 위 감지 시 지연 없이 즉시 혹은 동일하게 지연
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => setActiveSection("#"), 300);
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [location.pathname]);

  React.useEffect(() => {
    if (isLoggedIn) {
      const fetchStats = async () => {
        const count = await analyticsService.getTotalVisitorCount();
        setTotalVisitors(count);
      };
      fetchStats();
    }
  }, [isLoggedIn]);

  // 메뉴나 모달이 열려있을 때 배경 스크롤 방지
  React.useEffect(() => {
    if (isMenuOpen || showLoginModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen, showLoginModal]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");

    try {
      await login(email, password);
      setShowLoginModal(false);
      setEmail("");
      setPassword("");
    } catch (error: any) {
      setLoginError("로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleMenuClick = (href: string) => {
    // 1. 클릭 잠금 설정 (스크롤 감지 무시 시작)
    isClickScroll.current = true;

    // 2. 메뉴 즉시 활성화
    setActiveSection(href);

    const isMobile = window.innerWidth <= 1024;

    if (href === "#") {
      if (location.pathname !== "/") {
        navigate("/");
        if (isMobile) setIsMenuOpen(false);
      } else {
        if (isMobile) {
          setIsMenuOpen(false);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }, 300);
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    } else if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/" + href);
        if (isMobile) setIsMenuOpen(false);
      } else {
        if (isMobile) {
          setIsMenuOpen(false);
          setTimeout(() => {
            const element = document.querySelector(href);
            if (element) {
              scrollWithOffset(element);
            }
          }, 300);
        } else {
          const element = document.querySelector(href);
          if (element) {
            scrollWithOffset(element);
          }
        }
      }
    } else {
      navigate(href);
      if (isMobile) setIsMenuOpen(false);
    }

    // 3. 스크롤이 끝날 즈음(1초 후) 잠금 해제
    setTimeout(() => {
      isClickScroll.current = false;
    }, 1000);
  };

  return (
    <>
      <HeaderContainer className="header">
        <Container>
          <HeaderInner>
            <LogoArea onClick={() => handleMenuClick("/")}>
              <LogoKor>처음사랑교회</LogoKor>
              <LogoEng>
                <span>F</span>
                <span>I</span>
                <span>R</span>
                <span>S</span>
                <span>T</span>
                <span>&nbsp;</span>
                <span>L</span>
                <span>O</span>
                <span>V</span>
                <span>E</span>
                <span>&nbsp;</span>
                <span>C</span>
                <span>H</span>
                <span>U</span>
                <span>R</span>
                <span>C</span>
                <span>H</span>
              </LogoEng>
            </LogoArea>

            <DesktopMenu className="gnb">
              {MENU_ITEMS.map((item) => (
                <MenuItem
                  key={item.label}
                  className="gnb-item"
                  $isActive={activeSection === item.href}
                  onClick={() => handleMenuClick(item.href)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </DesktopMenu>

            <HeaderRight>
              {isLoggedIn &&
                (totalVisitors !== null ? (
                  <VisitorCount>
                    <FontAwesomeIcon
                      icon={faUsers}
                      style={{ marginRight: "6px" }}
                    />
                    누적 방문자 수 : {totalVisitors.toLocaleString()}
                  </VisitorCount>
                ) : (
                  <VisitorSkeleton>
                    <SkeletonPulse />
                  </VisitorSkeleton>
                ))}
              <DesktopAuth>
                {isLoggedIn ? (
                  <AuthButton className="logout-btn" onClick={handleLogout}>
                    로그아웃
                  </AuthButton>
                ) : (
                  <AuthButton
                    className="login-btn"
                    onClick={() => setShowLoginModal(true)}
                  >
                    로그인
                  </AuthButton>
                )}
              </DesktopAuth>
              <MobileIcon className="mobile-menu-btn" onClick={toggleMenu}>
                <FontAwesomeIcon
                  icon={isMenuOpen ? faTimes : faBars}
                  size="lg"
                />
              </MobileIcon>
            </HeaderRight>
          </HeaderInner>
        </Container>
      </HeaderContainer>

      <MobileMenuOverlay $isOpen={isMenuOpen}>
        <MobileMenuContent>
          {MENU_ITEMS.map((item) => (
            <MobileMenuItem
              key={item.label}
              $isActive={activeSection === item.href}
              onClick={() => handleMenuClick(item.href)}
            >
              {item.label}
            </MobileMenuItem>
          ))}
          <MobileAuth>
            {isLoggedIn ? (
              <MobileAuthButton onClick={handleLogout}>
                로그아웃
              </MobileAuthButton>
            ) : (
              <MobileAuthButton
                onClick={() => {
                  setShowLoginModal(true);
                  setIsMenuOpen(false);
                }}
              >
                로그인
              </MobileAuthButton>
            )}
          </MobileAuth>
        </MobileMenuContent>
      </MobileMenuOverlay>

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModalOverlay onClick={() => setShowLoginModal(false)}>
          <LoginModalContent onClick={(e) => e.stopPropagation()}>
            <LoginModalHeader>
              <LoginModalTitle>로그인</LoginModalTitle>
              <CloseButton onClick={() => setShowLoginModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </LoginModalHeader>
            <LoginForm onSubmit={handleLogin}>
              {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
              <FormGroup>
                <Label>이메일</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일을 입력하세요"
                  required
                  disabled={loggingIn}
                />
              </FormGroup>
              <FormGroup>
                <Label>비밀번호</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  required
                  disabled={loggingIn}
                />
              </FormGroup>
              <LoginButton type="submit" disabled={loggingIn}>
                {loggingIn ? "로그인 중..." : "로그인"}
              </LoginButton>
            </LoginForm>
          </LoginModalContent>
        </LoginModalOverlay>
      )}
    </>
  );
};

const HeaderContainer = styled.header`
  background: white;
  border-bottom: 1px solid #e0e0e0;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
  height: 90px;

  @media only screen and (max-width: 768px) {
    height: 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeaderInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 90px;

  @media only screen and (max-width: 768px) {
    height: 60px;
  }
`;

const LogoArea = styled.a`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  text-decoration: none;
  width: fit-content;
  cursor: pointer;
  height: 100%;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
`;

const LogoKor = styled.span`
  font-family: "ComputerLaundry", monospace;
  font-size: 26px;
  color: #004094;
  line-height: 1;
  margin-bottom: 4px;
  letter-spacing: -1px;
  white-space: nowrap;
  font-weight: 400;

  @media only screen and (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 2px;
  }
`;

const LogoEng = styled.span`
  font-family: "ComputerLaundry", monospace;
  font-size: 10px;
  color: #555;
  text-transform: uppercase;
  line-height: 1;
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-weight: 400;

  @media only screen and (max-width: 768px) {
    font-size: 8px;
  }
`;

const DesktopMenu = styled.nav`
  display: flex;
  gap: 30px;
  margin-left: auto;
  margin-right: 40px;

  @media only screen and (max-width: 1024px) {
    display: none;
  }
`;

const MenuItem = styled.div<{ $isActive?: boolean }>`
  font-size: 16px;
  color: ${({ $isActive }) => ($isActive ? "#004094" : "#333")};
  font-weight: ${({ $isActive }) => ($isActive ? "700 !important" : "300")};
  position: relative;
  padding: 10px 0;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #004094;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${({ $isActive }) => ($isActive ? "100%" : "0")};
    height: 2px;
    background: #004094;
    transition: 0.3s;
  }

  &:hover::after {
    width: 100%;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const VisitorCount = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
  background: #f5f5f5;
  padding: 6px 12px;
  border-radius: 20px;
  margin-right: 5px;

  @media only screen and (max-width: 768px) {
    font-size: 11px;
    padding: 4px 8px;
    margin-right: 0;
  }
`;

const DesktopAuth = styled.div`
  display: flex;
  margin-right: 20px;

  @media only screen and (max-width: 1024px) {
    display: none;
  }
`;

const AuthButton = styled.button`
  background: #004094;
  color: white;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: "Paperlogy", sans-serif;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;

  &:hover {
    background: #0056b3;
    transform: translateY(-1px);
  }
`;

const MobileAuth = styled.div`
  margin-top: 10px;
`;

const MobileAuthButton = styled.button`
  width: 100%;
  background: #004094;
  color: white;
  border: none;
  padding: 12px 20px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: "Paperlogy", sans-serif;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;

  &:hover {
    background: #0056b3;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px 16px;
  }
`;

const MobileIcon = styled.div`
  display: none;
  font-size: 24px;
  cursor: pointer;
  margin-left: 20px;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;

  @media only screen and (max-width: 1024px) {
    display: block;
  }

  @media only screen and (max-width: 768px) {
    font-size: 18px;
    margin-left: 10px;
  }
`;

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 90px;
  left: 0;
  width: 100%;
  height: calc(100vh - 90px);
  background-color: white;
  z-index: 999;
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(100%)"};
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);

  @media only screen and (max-width: 768px) {
    top: 60px;
    height: calc(100vh - 60px);
  }
`;

const MobileMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 30px;
  overflow-y: auto; /* 내용이 많아지면 스크롤 가능하도록 */
  height: 100%;
`;

const MobileMenuItem = styled.div<{ $isActive?: boolean }>`
  font-size: 20px;
  font-weight: ${({ $isActive }) => ($isActive ? "700" : "600")};
  color: ${({ $isActive }) => ($isActive ? "#004094" : "#333")};
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 15px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;

  &:hover {
    color: #004094;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    padding-bottom: 12px;
  }
`;

const LoginModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const LoginModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const LoginModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const LoginModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #004094;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  transition: color 0.3s;

  &:hover {
    color: #004094;
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #004094;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
`;

const LoginButton = styled.button`
  background: #004094;
  color: white;
  border: none;
  padding: 12px 20px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: "Paperlogy", sans-serif;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: #0056b3;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisitorSkeleton = styled.div`
  width: 150px;
  height: 31px;
  background: #f5f5f5;
  border-radius: 20px;
  margin-right: 5px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: 768px) {
    width: 120px;
    height: 25px;
    margin-right: 0;
  }
`;

const SkeletonPulse = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 0.05),
    transparent
  );
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

export default Header;
